import { format, parseISO } from "date-fns"
import { useEffect, useState } from "preact/hooks"
import { useRoute } from "wouter-preact"
import { usePageTitle } from "@/utils"

import { BlogAuthorByline } from "./author_byline"
import { BlogLayout } from "./blog_layout"
import { useBlogBootstrap } from "./bootstrap"
import { fetch_blog_post_by_slug } from "./fetch"
import { display_dimensions_for_image_width } from "./image_dimensions"
import { url_for_sanity_image } from "./image_url"
import { PortableTextBlock } from "./portable_text_block"
import type { BlogPostDetail } from "./types"

const HERO_IMG_WIDTH = 1200

/** Reserve space when Sanity has not stored dimensions yet (~1.91:1). */
const HERO_FALLBACK_HEIGHT = 630

function bootstrap_post_has_body(p: BlogPostDetail | null | undefined): boolean {
  return Array.isArray(p?.body) && p.body.length > 0
}

export function BlogPostPage() {
  const [, params] = useRoute("/blog/:slug")
  const slug = params?.slug ?? ""

  const bootstrap = useBlogBootstrap()

  const [post, setPost] = useState<BlogPostDetail | null | undefined>(() => {
    if (bootstrap?.kind !== "post") {
      return undefined
    }
    if (bootstrap.post && bootstrap.post.slug === slug) {
      if (bootstrap_post_has_body(bootstrap.post)) {
        return bootstrap.post
      }
      return undefined
    }
    if (bootstrap.post === null) {
      return null
    }
    return undefined
  })
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(() => {
    if (bootstrap?.kind === "post" && bootstrap.post?.slug === slug) {
      return !bootstrap_post_has_body(bootstrap.post)
    }
    if (bootstrap?.kind === "post" && bootstrap.post === null) {
      return true
    }
    return Boolean(slug)
  })

  useEffect(() => {
    if (!slug) {
      return
    }
    if (bootstrap?.kind === "post" && bootstrap.post?.slug === slug && bootstrap_post_has_body(bootstrap.post)) {
      setPost(bootstrap.post)
      setError(null)
      setLoading(false)
      return
    }
    // Do not skip fetch when SSR had `post: null` — post may exist now (published after build / no env at prerender).
    let cancelled = false
    setLoading(true)
    fetch_blog_post_by_slug(slug)
      .then((p) => {
        if (!cancelled) {
          setPost(p)
          setError(null)
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [slug, bootstrap])

  const title_for_chrome = post?.seo?.metaTitle || post?.title || "Blog post"
  usePageTitle(title_for_chrome)

  const hero = url_for_sanity_image(post?.mainImage, HERO_IMG_WIDTH)
  const hero_dims =
    display_dimensions_for_image_width(post?.mainImage, HERO_IMG_WIDTH) ??
    ({ width: HERO_IMG_WIDTH, height: HERO_FALLBACK_HEIGHT } as const)

  return (
    <BlogLayout>
      <article class="mx-auto max-w-3xl">
        <nav class="text-sm text-indigo-700" aria-label="Breadcrumb">
          <a href="/" class="text-indigo hover:underline">
            Home
          </a>
          <span class="mx-2 text-indigo-400">/</span>
          <a href="/blog" class="text-indigo hover:underline">
            Blog
          </a>
        </nav>

        {error ? (
          <p class="mt-8 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">Could not load this post.</p>
        ) : null}

        {!error && loading && !post ? (
          <div class="mt-10 space-y-6 animate-pulse" aria-hidden="true">
            <div class="h-4 w-32 rounded bg-sky-100" />
            <div class="h-12 w-full max-w-xl rounded bg-sky-100" />
            <div class="h-4 w-full rounded bg-sky-50" />
            <div class="aspect-21/9 w-full rounded-3xl bg-sky-100" />
          </div>
        ) : null}

        {!error && !loading && !post ? (
          <div class="mt-10">
            <h1 class="font-display text-4xl font-bold tracking-tight text-indigo-950 md:text-5xl">Not found</h1>
            <p class="mt-4 text-lg text-indigo-800">This post does not exist or was removed.</p>
            <a
              href="/blog"
              class="mt-8 inline-flex rounded-full border border-indigo bg-indigo px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-white hover:opacity-95">
              ← Back to blog
            </a>
          </div>
        ) : null}

        {post ? (
          <>
            <header class="mt-8">
              <p class="text-xs font-bold uppercase tracking-[0.2em] text-salmon">Blog</p>
              <div class="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
                <BlogAuthorByline authorSlug={post.author} size="md" />
                <time class="text-sm font-semibold uppercase tracking-wide text-indigo-600 sm:border-l sm:border-sky-200 sm:pl-8" dateTime={post.publishedAt}>
                  {safe_format_date(post.publishedAt)}
                </time>
              </div>
              <h1 class="font-display mt-6 text-4xl font-bold tracking-tight text-indigo-950 md:text-5xl lg:text-[3.25rem] lg:leading-[1.1]">
                {post.title}
              </h1>
              {post.excerpt ? (
                <p class="mt-5 text-xl font-medium leading-relaxed text-indigo-900 md:text-2xl md:leading-snug">{post.excerpt}</p>
              ) : null}
            </header>

            {hero ? (
              <figure class="mt-10 overflow-hidden rounded-3xl border border-sky-100 bg-white shadow-md ring-1 ring-sky-100/80">
                <img
                  src={hero}
                  alt={post.mainImage?.alt ?? ""}
                  width={hero_dims.width}
                  height={hero_dims.height}
                  class="h-auto w-full object-cover"
                />
              </figure>
            ) : null}

            <div class="mt-12 text-lg leading-relaxed text-indigo-900">
              <PortableTextBlock value={post.body} />
            </div>

            <footer class="mt-16 border-t border-sky-200 pt-10">
              <a
                href="/blog"
                class="inline-flex items-center rounded-full border border-indigo px-4 py-2 text-xs font-bold uppercase tracking-wide text-indigo transition-colors hover:bg-indigo hover:text-white">
                ← All posts
              </a>
            </footer>
          </>
        ) : null}
      </article>
    </BlogLayout>
  )
}

function safe_format_date(iso: string): string {
  try {
    return format(parseISO(iso), "MMMM d, yyyy")
  } catch {
    return iso
  }
}
